import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Modal, Button } from '@components/ui';
import CompanySelector from '@components/shared/CompanySelector';
import mockBlocks, { getBlocksByConstructionId } from '@mocks/bloklar';
import { mockCompanies } from '@mocks/firmalar';
import { calculateKDV, DEFAULT_KDV_RATE } from '@utils/kdv';

export default function AsansorModal({ isOpen, onClose, onSubmit, onMaterialSubmit, submitLabel, initialData = null, isLoading = false, insaatId }) {
    const isEdit = !!initialData;
    const [selectedCompany, setSelectedCompany] = useState(
        initialData?.supplierId ? { id: initialData.supplierId, name: initialData.tedarikci } : null
    );

    let availableBlocks = [];
    if (insaatId) {
        availableBlocks = getBlocksByConstructionId(insaatId).map(b => ({ id: b.id, name: b.name }));
    } else {
        availableBlocks = mockBlocks.map(b => ({ id: b.id, name: `${b.constructionName} - ${b.name}` }));
    }

    const { register, handleSubmit, reset, watch, formState: { errors } } = useForm({
        defaultValues: initialData || { siparisTarihi: new Date().toISOString().split('T')[0], durum: 'bekliyor', montajIlerleme: 0, kdvOrani: DEFAULT_KDV_RATE },
    });

    const watchedTutar = watch('toplamFiyat');

    const handleFormSubmit = (data) => {
        if (!insaatId) {
            const selectedBlock = availableBlocks.find(b => b.name === data.blokAdi);
            if (selectedBlock) data.insaatId = selectedBlock.constructionId;
        } else { data.insaatId = insaatId; }
        data.miktar = 1;
        data.birim = 'adet';
        data.kdvDahilToplam = data.toplamFiyat;
        data.tedarikci = selectedCompany?.name || data.tedarikci;
        data.supplierId = selectedCompany?.id || null;

        const enriched = {
            ...data,
            selectedCompany,
            calculatedTotal: data.toplamFiyat,
            autoDescription: `Asansör — ${data.model} ${data.kapasite}`,
        };

        if (onMaterialSubmit) {
            onMaterialSubmit(enriched);
        } else {
            onSubmit(enriched, isEdit);
        }
    };

    const handleClose = () => { reset(); onClose(); };
    const inputClass = "w-full px-3 py-2.5 bg-bg-primary border border-border-light rounded-lg text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-primary/50 transition-colors";
    const errorClass = "border-error focus:ring-error/50";

    return (
        <Modal isOpen={isOpen} onClose={handleClose} title={isEdit ? 'Asansör Kaydı Düzenle' : 'Yeni Asansör Kaydı'} size="lg"
            footer={<><Button variant="ghost" onClick={handleClose} disabled={isLoading}>İptal</Button><Button variant="primary" onClick={handleSubmit(handleFormSubmit)} isLoading={isLoading}>{submitLabel || (isEdit ? 'Güncelle' : 'Kayıt Oluştur')}</Button></>}>
            <form className="space-y-5">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <CompanySelector value={selectedCompany} onChange={setSelectedCompany} companies={mockCompanies} label="Tedarikçi / Firma" />
                    <div><label className="block text-sm font-medium text-text-primary mb-1.5">Sipariş Tarihi</label><input type="date" {...register('siparisTarihi')} className={inputClass} /></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><label className="block text-sm font-medium text-text-primary mb-1.5">Marka <span className="text-error">*</span></label>
                        <input type="text" {...register('marka', { required: true })} placeholder="Otis, Kone, Schindler..." className={`${inputClass} ${errors.marka ? errorClass : ''}`} /></div>
                    <div><label className="block text-sm font-medium text-text-primary mb-1.5">Model <span className="text-error">*</span></label>
                        <input type="text" {...register('model', { required: true })} placeholder="Gen2 Comfort" className={`${inputClass} ${errors.model ? errorClass : ''}`} /></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><label className="block text-sm font-medium text-text-primary mb-1.5">Blok <span className="text-error">*</span></label>
                        <select {...register('blokAdi', { required: true })} className={inputClass}>
                            <option value="">Seçiniz</option>
                            {availableBlocks.map(b => (<option key={b.id} value={b.name}>{b.name}</option>))}
                        </select></div>
                    <div><label className="block text-sm font-medium text-text-primary mb-1.5">Kapasite</label>
                        <input type="text" {...register('kapasite')} placeholder="630 kg / 8 kişi" className={inputClass} /></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div><label className="block text-sm font-medium text-text-primary mb-1.5">Kat Sayısı</label><input type="number" {...register('kat', { valueAsNumber: true })} placeholder="6" className={inputClass} /></div>
                    <div><label className="block text-sm font-medium text-text-primary mb-1.5">Durak Sayısı</label><input type="number" {...register('durakSayisi', { valueAsNumber: true })} placeholder="7" className={inputClass} /></div>
                    <div><label className="block text-sm font-medium text-text-primary mb-1.5">Tutar (₺)</label><input type="number" {...register('toplamFiyat', { valueAsNumber: true })} placeholder="450000" className={inputClass} /></div>
                    <div><label className="block text-sm font-medium text-text-primary mb-1.5">Durum</label>
                        <select {...register('durum')} className={inputClass}>
                            <option value="bekliyor">Bekliyor</option><option value="uretim">Üretimde</option><option value="montaj">Montajda</option><option value="teslim">Teslim / Aktif</option>
                        </select></div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div><label className="block text-sm font-medium text-text-primary mb-1.5">Garanti Süresi</label><input type="text" {...register('garanti')} placeholder="24 ay" className={inputClass} /></div>
                    <div><label className="block text-sm font-medium text-text-primary mb-1.5">Bakım Sözleşmesi</label><select {...register('bakimSozlesmesi')} className={inputClass}><option value={true}>Var</option><option value={false}>Yok</option></select></div>
                </div>
                <div><label className="block text-sm font-medium text-text-primary mb-1.5">Not</label><textarea {...register('not')} rows={2} placeholder="Asansör notu..." className={`${inputClass} resize-none`} /></div>
            </form>
        </Modal>
    );
}
